import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import WbSunnyIcon from "@mui/icons-material/WbSunny"; // Morning icon
import Brightness5Icon from "@mui/icons-material/Brightness5"; // Afternoon icon
import NightsStayIcon from "@mui/icons-material/NightsStay"; // Evening icon

// Styled components
const DateButton = styled(Button)(({ theme, selected }) => ({
  borderRadius: "8px",
  margin: "4px",
  padding: "8px 12px",
  backgroundColor: selected ? theme.palette.primary.main : "#F5F5F5",
  color: selected ? "#FFFFFF" : theme.palette.text.primary,
  textTransform: "none",
  "&:hover": {
    backgroundColor: selected ? theme.palette.primary.dark : "#E0E0E0",
  },
}));

const SlotButton = styled(Button)(({ theme, selected }) => ({
  margin: "4px",
  padding: "8px 16px",
  borderRadius: "8px",
  backgroundColor: selected ? theme.palette.primary.main : "#F5F5F5",
  color: selected ? "#FFFFFF" : theme.palette.text.primary,
  textTransform: "none",
  "&:hover": {
    backgroundColor: selected ? theme.palette.primary.dark : "#E0E0E0",
  },
}));

const SlotDialog = ({ open, onClose, onSelectSlot }) => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Generate dates for the next 30 days
  const dates = Array.from({ length: 30 }, (_, index) =>
    dayjs().add(index, "day")
  );

  // Time slots for the day
  const timeSlots = {
    Morning: [
      { label: "6:00 AM - 6:30 AM", value: "06:30:00" },
      { label: "6:30 AM - 7:00 AM", value: "07:00:00" },
      { label: "8:00 AM - 8:30 AM", value: "08:30:00" },
    ],
    Afternoon: [
      { label: "12:00 PM - 12:30 PM", value: "12:30:00" },
      { label: "1:00 PM - 1:30 PM", value: "13:30:00" },
      { label: "2:00 PM - 2:30 PM", value: "14:30:00" },
    ],
    Evening: [
      { label: "6:00 PM - 6:30 PM", value: "18:30:00" },
      { label: "7:00 PM - 7:30 PM", value: "19:30:00" },
      { label: "8:00 PM - 8:30 PM", value: "20:30:00" },
    ],
  };

  // Filter slots based on current time for today
  const filterSlots = (slots) => {
    if (!selectedDate.isSame(dayjs(), "day")) {
      return slots; // All slots available for future dates
    }
    const currentTime = dayjs();
    return slots.filter((slot) =>
      currentTime.isBefore(selectedDate.format("YYYY-MM-DD") + "T" + slot.value)
    );
  };

  const renderIcon = (timeOfDay) => {
    if (timeOfDay === "Morning") return <WbSunnyIcon color="primary" />;
    if (timeOfDay === "Afternoon") return <Brightness5Icon color="primary" />;
    if (timeOfDay === "Evening") return <NightsStayIcon color="primary" />;
    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select a Slot</DialogTitle>
      <DialogContent>
        {/* Horizontal Date Selector */}
        <Box display="flex" overflow="auto" mb={3}>
          {dates.map((date) => (
            <DateButton
              key={date.format("YYYY-MM-DD")}
              selected={date.isSame(selectedDate, "day")}
              onClick={() => {
                setSelectedDate(date);
                setSelectedSlot(null); // Reset slot on date change
              }}
            >
              {date.format("DD MMM")}
            </DateButton>
          ))}
        </Box>

        {/* Time Slots */}
        {Object.entries(timeSlots).map(([timeOfDay, slots]) => {
          const filteredSlots = filterSlots(slots);
          return (
            filteredSlots.length > 0 && (
              <Box key={timeOfDay} mb={3}>
                <Box display="flex" alignItems="center" mb={1}>
                  {renderIcon(timeOfDay)}
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    ml={1}
                    color="text.secondary"
                  >
                    {timeOfDay}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {filteredSlots.map((slot) => (
                    <Grid item key={slot.value}>
                      <SlotButton
                        selected={selectedSlot === slot.value}
                        onClick={() => setSelectedSlot(slot.value)}
                      >
                        {slot.label}
                      </SlotButton>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my: 2 }} />
              </Box>
            )
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedSlot}
          onClick={() => {
            onSelectSlot(`${selectedDate.format("YYYY-MM-DD")} ${selectedSlot}`);
            onClose();
          }}
        >
          Confirm Slot
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SlotDialog;